import { useEffect } from 'react'
import { useAppSetting, useUpdateAppSetting } from '@/components/appStore'
import { getSearchParams } from 'core/utils'

/**
 * @description 同步URL中的主题参数与本地存储的主题设置，并在设置后移除URL中的参数
 * 该Hook用于：
 * 1. 监听URL中的theme或skin参数变化
 * 2. 当URL中的主题参数与本地存储不一致时，更新本地主题设置
 * 3. 更新完成后移除URL中的主题参数
 * 4. 支持两种参数格式：
 *    - theme: 'light' | 'dark'
 *    - skin: '1'(深色) | '2'(浅色)
 *
 * @example
 * ```tsx
 * const App = () => {
 *   useSyncThemeWithQuery()
 *   return <div>...</div>
 * }
 * ```
 */
const useSyncThemeWithQuery = () => {
  const theme = useAppSetting('theme')
  const updateAppSetting = useUpdateAppSetting()

  useEffect(() => {
    // 获取URL中的主题参数
    const themeQuery = getSearchParams('theme')
    const skinQuery = getSearchParams('skin')

    let newTheme = theme

    // 处理 theme 参数
    if (themeQuery && themeQuery !== theme && ['light', 'dark'].includes(themeQuery)) {
      newTheme = themeQuery
    }

    // 处理 skin 参数
    if (skinQuery) {
      switch (skinQuery) {
        case '1':
          newTheme = 'dark'
          break
        case '2':
          newTheme = 'light'
          break
      }
    }

    // 如果需要更新主题
    if (newTheme !== theme) {
      // 更新主题设置
      updateAppSetting('theme', newTheme)

      // 移除URL中的主题参数
      const url = new URL(window.location.href)
      url.searchParams.delete('theme')
      url.searchParams.delete('skin')
      window.history.replaceState(null, '', url.toString())
    }
  }, [theme, updateAppSetting])
}

export default useSyncThemeWithQuery
